import { isValidNumber, validatePhoneNumberLength } from 'libphonenumber-js';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { createUserAction } from '../redux/actions/user-actions';

import { Formik } from 'formik';
import * as Yup from 'yup';

import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  List,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import IconButton from '@material-ui/core/IconButton/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment/InputAdornment';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { BackNavigationComponent } from '../../../../core/routes/components/back-navigation-component';

import store from '../../../../core/redux/store';
import { ScopeCheckboxItem } from '../components/scope-checkbox-item';
import {
  alertsScopes,
  groupedScopes,
  paymentScopes,
  Scope,
  scopeGroupLabels
} from '../../../../core/types/scopes';
import { getReportsAction } from '../../../reports/presentation/redux/actions/reports-actions';

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  },
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionHeading: {
    margin: theme.spacing(2)
  },
  margin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  formButtons: {
    marginTop: theme.spacing(4)
  },
  errorText: {
    color: '#f44336',
    textAlign: 'center',
    display: 'block',
    margin: theme.spacing(3)
  },
  reportRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

function ReportsGroup({ reports, selectedReports, updateSelectedReports }) {
  const classes = useStyles();
  return (
    <Grid container>
      {reports.map((report) => {
        return (
          <Grid
            container
            alignItems="center"
            xs={12}
            sm={6}
            direction="row"
            key={report.id}
            spacing={2}
            className={classes.reportRow}
            onClick={() => updateSelectedReports(report.id)}
          >
            <Grid item>
              <Checkbox
                checked={selectedReports.includes(report.id)}
                onChange={() => updateSelectedReports(report.id)}
              />
            </Grid>
            <Grid item xs>
              <Typography>{report.name}</Typography>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
}

function CreateUserForm(props) {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const state = store.getState();
  const token = state.token.token;

  const [showPassword, setShowPassword] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [groups, setGroups] = useState({ ...groupedScopes });

  useEffect(() => {
    if (!token) return;
    dispatch(getReportsAction(token));
  }, []);

  const { error, loading } = useSelector((state) => state.user);
  const { me } = useSelector((state) => state.me);
  const { reports } = useSelector((state) => state.reports);

  const { values, errors, touched, handleSubmit, handleChange, handleBlur, setFieldValue } = props;

  /**
   * @param {string} name
   */
  const formikProps = (name, initialValue = '') => ({
    name: name,
    value: typeof values[name] !== 'undefined' ? values[name] : initialValue,
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] && Boolean(errors[name]),
    helperText: touched[name] ? errors[name] : ''
  });

  useEffect(() => {
    setFieldValue('reports', selectedReports);
  }, [selectedReports]);

  useEffect(() => {
    if (me) {
      const { allow_custom_payment_fields, flightboard_enabled } = me.organization;

      if (allow_custom_payment_fields) {
        setGroups((state) => ({
          ...state,
          [scopeGroupLabels.reports]: [
            ...new Set([...state[scopeGroupLabels.reports], ...paymentScopes])
          ]
        }));
      }
      if (flightboard_enabled) {
        setGroups((state) => ({
          ...state,
          [scopeGroupLabels.alerts]: [
            ...new Set([...state[scopeGroupLabels.alerts], ...alertsScopes])
          ]
        }));
      }
    }
  }, [me]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleScopeChange = (event) => {
    const scope = event.target.value;
    const updatedScopes = values.scopes.includes(scope)
      ? values.scopes.filter((s) => s !== scope)
      : [...values.scopes, scope];

    handleChange({
      target: { name: 'scopes', value: updatedScopes }
    });
  };

  const updateSelectedReports = (reportId) => {
    const exists = selectedReports.includes(reportId);

    if (exists) {
      setSelectedReports(selectedReports.filter((id) => id !== reportId));
    } else {
      setSelectedReports([...selectedReports, reportId]);
    }
  };

  return (
    <form className={classes.root} noValidate autoComplete="off" onSubmit={handleSubmit}>
      <TextField
        {...formikProps('firstname')}
        id="create-user-page-firstname"
        label="First Name"
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <TextField
        {...formikProps('lastname')}
        id="create-user-page-lastname"
        label="Last Name"
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <TextField
        {...formikProps('email')}
        id="create-user-page-email"
        label="Email"
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <TextField
        {...formikProps('mobile_no')}
        id="create-user-page-mobile-no"
        label="Phone no."
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <TextField
        {...formikProps('password')}
        id="create-user-page-password"
        label="Password"
        helperText={
          (touched.password && errors.password) ||
          "Leave blank if this person already has a Loop login: they'll keep their existing password."
        }
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        variant="outlined"
        fullWidth={true}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <FormControl component="fieldset" className={classes.root} fullWidth>
        <FormLabel component="legend" style={{ fontWeight: 'bold', marginBottom: 10 }}>
          Permissions
        </FormLabel>

        <Grid container spacing={2} style={{ marginBottom: 15 }}>
          <Grid item>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() =>
                handleChange({ target: { name: 'scopes', value: Object.values(Scope) } })
              }
            >
              Select All
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={() => handleChange({ target: { name: 'scopes', value: [] } })}
            >
              Deselect All
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3} style={{ width: '100%' }}>
          {Object.entries(groups).map(([category, scopeList]) => {
            if (scopeList.length === 0) return null; // Skip empty categories
            return (
              <Grid item xs={12} md={4} key={category} className={classes.column}>
                <Typography variant="subtitle1" className={classes.sectionHeading}>
                  {category}
                </Typography>
                <List>
                  {scopeList.map((scope) => (
                    <ScopeCheckboxItem
                      key={scope}
                      scope={scope}
                      checked={values.scopes.includes(scope)}
                      onChange={handleScopeChange}
                    />
                  ))}
                </List>
              </Grid>
            );
          })}
        </Grid>
      </FormControl>

      <FormControl component="fieldset" className={classes.root} fullWidth>
        <FormLabel component="legend" style={{ fontWeight: 'bold', marginBottom: 10 }}>
          Reports
        </FormLabel>
        <Grid container spacing={2} style={{ marginBottom: 15 }}>
          <ReportsGroup
            reports={reports}
            selectedReports={selectedReports}
            updateSelectedReports={updateSelectedReports}
          />
        </Grid>
      </FormControl>
      <div className={classes.formButtons}>
        {!loading && (
          <>
            <Button
              className={classes.margin}
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Create User'}
            </Button>
            <Button
              className={classes.margin}
              variant="outlined"
              size="large"
              color="secondary"
              fullWidth={true}
              onClick={() => history.push('/users')}
            >
              Cancel
            </Button>
          </>
        )}

        {loading && (
          <Button
            className={classes.margin}
            variant="contained"
            size="large"
            color="primary"
            fullWidth={true}
          >
            <CircularProgress color="inherit" size={26} />
          </Button>
        )}

        {error && (
          <div>
            <span className={classes.errorText}>{error.message}</span>
          </div>
        )}
      </div>
    </form>
  );
}

const validationSchema = Yup.object({
  firstname: Yup.string()
    .trim()
    .required('First name is required')
    .matches(/^[a-zA-Z\s]+$/, 'First name can only contain letters and spaces'),

  lastname: Yup.string()
    .trim()
    .required('Last name is required')
    .matches(/^[a-zA-Z\s]+$/, 'Last name can only contain letters and spaces'),

  email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),

  mobile_no: Yup.string()
    .trim()
    .required('Phone number is required')
    .test(
      'is-valid-phone',
      'Enter a valid phone number with country code (e.g., +9723656759)',
      (value) => {
        if (!value) return false;
        try {
          return isValidNumber(value) && validatePhoneNumberLength(value) === undefined;
        } catch (err) {
          return false;
        }
      }
    ),

  // Optional: an email that already has a Loop login (at another client) keeps that login's password.
  password: Yup.string()
    .trim()
    .matches(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^A-Za-z0-9\s]).{8,}$/, {
      message:
        'Password must have at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character',
      excludeEmptyString: true
    }),

  scopes: Yup.array().of(Yup.string()).default([])
});

function CreateUserFormValidation() {
  const history = useHistory();
  const dispatch = useDispatch();

  const initialValues = {
    firstname: '',
    lastname: '',
    email: '',
    mobile_no: '',
    password: '',
    scopes: [],
    reports: []
  };

  const submit = async (data, { setSubmitting, resetForm, setErrors }) => {
    try {
      setSubmitting(true);
      const props = { history };

      await dispatch(
        createUserAction(
          data.firstname.trim(),
          data.lastname.trim(),
          data.email.trim(),
          data.mobile_no.trim(),
          data.password,
          data.scopes,
          data.reports,
          props
        )
      );

      resetForm();
      history.push('/users');
    } catch (error) {
      setErrors({ submit: error.message || 'An error occurred while creating the user' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
      {(props) => <CreateUserForm {...props} />}
    </Formik>
  );
}

export default function CreateUserPage() {
  const classes = useStyles();

  return (
    <React.Fragment>
      <CssBaseline />
      <Container className={classes.container}>
        <BackNavigationComponent
          page="Create user"
          uri="/users"
          path={[
            {
              page: 'Home',
              uri: '/'
            },
            {
              page: 'Users',
              uri: '/users'
            }
          ]}
        />
        <Grid container spacing={2} justify="center">
          <Grid item xs={12} md={8}>
            <CreateUserFormValidation />
          </Grid>
        </Grid>
      </Container>
    </React.Fragment>
  );
}
